import React, { useState, useCallback, useEffect } from 'react';
import cn from 'classnames';
import PT from 'prop-types';
import ReactSelect, { components } from 'react-select';
import AsyncSelect from 'react-select/async';
import Icon from '../Icon';
import DatePicker from '../DatePicker';
import 'react-day-picker/lib/style.css';
import InputMask from 'react-input-mask';

const DropdownIndicator = ({ ...props }) => {
  return (
    <components.DropdownIndicator {...props}>
      <Icon name={props.selectProps.menuIsOpen ? 'icon-up' : 'icon-down'} />
    </components.DropdownIndicator>
  );
};

export const CustomSelect = ({
  onBlur,
  onChange,
  onOpen,
  options,
  value,
  loadOptions,
  defaultOptions,
  variantSelect,
  onInputChange,
  alwaysOpenMenu,
  ...fieldProps
}) => {
  const [isMenuOpened, setIsMenuOpened] = useState(alwaysOpenMenu || false);

  const handleMenuOpen = useCallback(() => {
    !alwaysOpenMenu && setIsMenuOpened(true);
    onOpen && onOpen();
  }, [alwaysOpenMenu, onOpen]);

  const handleMenuClose = useCallback(() => {
    !alwaysOpenMenu && setIsMenuOpened(false);
  }, [alwaysOpenMenu]);

  if (variantSelect === 'AsyncSelect') {
    return (
      <AsyncSelect
        isSearchable={false}
        {...fieldProps}
        components={{ IndicatorSeparator: () => null, DropdownIndicator, ...(fieldProps.components || {}) }}
        classNamePrefix="input-group"
        onBlur={onBlur}
        defaultOptions={defaultOptions}
        loadOptions={loadOptions}
        onMenuOpen={handleMenuOpen}
        onMenuClose={handleMenuClose}
        menuIsOpen={isMenuOpened}
        closeMenuOnSelect={true}
        onChange={onChange}
        value={value}
      />
    );
  } else {
    return (
      <ReactSelect
        isSearchable={false}
        {...fieldProps}
        components={{ IndicatorSeparator: () => null, DropdownIndicator, ...(fieldProps.components || {}) }}
        classNamePrefix="input-group"
        onBlur={onBlur}
        options={options}
        onMenuOpen={handleMenuOpen}
        onMenuClose={handleMenuClose}
        menuIsOpen={isMenuOpened}
        closeMenuOnSelect={true}
        onChange={onChange}
        value={value}
        onInputChange={onInputChange}
      />
    );
  }
};

const Input = React.forwardRef((props, ref) => {
  const {
    label,
    name,
    variant = '',
    helper,
    helperVariant = 'message',
    inputMask,
    maskChar,
    fieldProps = {},
    icon,
    prefix,
    disabled,
    className,
    callback,
    options,
    key,
    inputFileIcon,
    initialFocusState = false,
    id,
    variantSelect,
    loadOptions,
    onInputChange,
    defaultOptions,
    acceptTypes,
    dataTestId,
  } = props;

  const [isFocused, setIsFocused] = useState(false);
  const [isOpenDatePicker, setIsOpenDatePicker] = useState(false);
  const [inputValue, setInputValue] = useState();
  const [inputFileName, setInputFileName] = useState({
    name: 'No file chosen',
    style: null,
  });

  useEffect(() => {
    setInputValue(fieldProps.value);
  }, [fieldProps]);

  const handleFocus = useCallback(
    (...args) => {
      const { onFocus } = fieldProps;

      setIsFocused(true);

      if (onFocus) {
        onFocus(...args);
      }
    },
    [fieldProps]
  );

  const handleBlur = useCallback(
    (...args) => {
      const { onBlur } = fieldProps;

      setIsFocused(false);

      if (onBlur) {
        onBlur(...args);
      }
    },
    [fieldProps]
  );

  const handleChange = useCallback(
    (...args) => {
      const { onChange } = fieldProps;

      const value = args[0].target.value;

      setInputValue(value);

      if (onChange) {
        onChange(...args);
      }
    },
    [fieldProps]
  );

  const addInputLabel = (e) => {
    setInputFileName({ name: e.target.files[0].name, style: 'chozenFile' });
  };

  const handleClickOnIcon = useCallback(
    (status) => {
      switch (variant) {
        case 'date':
          setIsOpenDatePicker(status);
          break;
        default:
          break;
      }
    },
    [variant]
  );

  let field;

  switch (variant) {
    case '':
      field = (
        <InputMask
          {...fieldProps}
          className={cn('input-group__field', {
            _warning: helperVariant === 'warning',
            _message: helperVariant === 'message',
            _error: helperVariant === 'error',
          })}
          onChange={handleChange}
          onBlur={handleBlur}
          onFocus={handleFocus}
          mask={inputMask}
          maskChar={maskChar}
          autoComplete={'off'}
          autoFocus={initialFocusState}
          value={inputValue === null || inputValue === undefined ? '' : inputValue}
          label={label}
          key={key}
          ref={ref}
          id={fieldProps.name}
          data-testid={id}
        />
      );
      break;

    case 'textarea':
      field = (
        <textarea
          {...fieldProps}
          className={'input-group__field'}
          onChange={(handleChange, (e) => callback(e))}
          onBlur={handleBlur}
          onFocus={handleFocus}
          ref={ref}
          id={fieldProps.name}
          data-testid={id}
        />
      );
      break;

    case 'select':
      field = (
        <CustomSelect
          {...fieldProps}
          options={options}
          defaultOptions={defaultOptions}
          onChange={(selectedOption) => callback(selectedOption)}
          onInputChange={onInputChange}
          value={inputValue}
          onFocus={handleFocus}
          onBlur={handleBlur}
          loadOptions={loadOptions}
          variantSelect={variantSelect}
          id={fieldProps.name}
        />
      );
      break;

    case 'date':
      field = (
        <DatePicker
          {...fieldProps}
          className={cn('input-group__field', {
            _warning: helperVariant === 'warning',
            _message: helperVariant === 'message',
            _error: helperVariant === 'error',
          })}
          onInputFocus={handleFocus}
          onInputBlur={handleBlur}
          ref={ref}
          dayPickerProps={{
            firstDayOfWeek: 1,
          }}
          openCalendar={isOpenDatePicker}
          callbackHandleOpenCalendar={handleClickOnIcon}
        />
      );
      break;
    case 'input-file':
      field = (
        <div className="input-file">
          <span className={cn('input-file__icon', inputFileIcon)}></span>
          <label htmlFor={id} className="input-file__label">
            {name}
            <input
              id={id}
              data-testid={id}
              className="input-file__item"
              accept={acceptTypes}
              type="file"
              name={id}
              multiple
              onChange={(e) => {
                addInputLabel(e);
                callback(e);
              }}
            />
          </label>
          <p className={inputFileName.style}>{inputFileName.name}</p>
        </div>
      );
      break;

    default:
      break;
  }

  return (
    <div
      data-testid={dataTestId}
      className={cn(
        'input-group',
        {
          [`input-group_${variant}`]: variant.length,
          'input-group_disabled': disabled,
          'input-group_with-icon': icon,
          'input-group_with-prefix': prefix,
        },
        className
      )}
    >
      {label && (
        <label className="input-group__label" htmlFor={fieldProps.name}>
          {label}
        </label>
      )}
      <div
        className={cn('input-group__container', {
          _warning: helperVariant === 'warning',
          _message: helperVariant === 'message',
          _error: helperVariant === 'error',
          _focus: isFocused,
        })}
        data-testid="input-group"
      >
        {prefix && <span className={'input-group__field-mask'}>{prefix}</span>}
        {field}
        {icon && (
          <div className="input-group__icon-container" onClick={() => handleClickOnIcon(true)}>
            {icon}
          </div>
        )}
      </div>
      {helper && (
        <span className={cn('input-group__helper', `_${helperVariant}`)} data-testid={dataTestId}>
          {helper}
        </span>
      )}
    </div>
  );
});

Input.propTypes = {
  className: PT.string,
  label: PT.oneOfType([PT.string, PT.number]),
  variant: PT.oneOf(['textarea', 'input', 'select', 'date', 'input-file']),
  helper: PT.oneOfType([PT.string, PT.number]),
  helperVariant: PT.oneOf(['message', 'error', 'warning']),
  fieldProps: PT.object,
  icon: PT.object,
  prefix: PT.string,
  disabled: PT.bool,
};

export default Input;
